/**
 * CR130: Deal Card UX Context
 * CR500 Phase 2B: Added detailsPresentation for presentation mode control
 * 
 * Provides cardSize, density, and detailsMode configuration to DealGiftCard
 * components throughout the application. Values come from the active Layout Preset.
 */

import { createContext, useContext, type ReactNode } from "react";
import { 
  type CardSize, 
  type CardDensity,
  type DetailsMode,
  type DetailsSectionConfig,
  type CustomHeightsPx,
  type FlyerInteractionConfig,
  type FeedbackWidgetConfig,
  CARD_SIZE_HEIGHTS,
  DENSITY_SPACING,
  DEFAULT_DEAL_CARD_UX_CONFIG,
  DEFAULT_FLYER_INTERACTION_CONFIG,
  DEFAULT_FEEDBACK_WIDGET_CONFIG,
  getDealCardHeightPx,
} from "@/lib/layout-studio/dealCardUXTypes";
import type { DetailsPresentationConfig } from "@/lib/layout-studio/detailsPresentationTypes";

// ================================================================
// CONTEXT TYPE
// ================================================================

interface DealCardUXContextValue {
  cardSize: CardSize;
  density: CardDensity;
  /** Computed height in pixels for current cardSize (respects custom heights) */
  cardHeightPx: number;
  /** Computed spacing values for current density */
  spacing: typeof DENSITY_SPACING[CardDensity];
  /** CR130 Phase 3: Details mode (legacy - use detailsPresentation.mode) */
  detailsMode: DetailsMode;
  /** CR130 Phase 3: Whether only one card can be expanded at a time */
  expandSingleOpen: boolean;
  /** CR130 Phase 3: Details sections configuration (legacy - use v2 spec) */
  detailsSections: DetailsSectionConfig[];
  /** CR171: Whether custom heights are enabled */
  customHeightsEnabled: boolean;
  /** CR171: Custom heights per tier */
  customHeightsPx: CustomHeightsPx;
  /** CR500 Phase 2B: Presentation mode configuration */
  detailsPresentation?: DetailsPresentationConfig;
  /** CR451: Flyer interaction config */
  flyerInteraction: FlyerInteractionConfig;
  /** CR508: Global feedback widget placement config */
  feedbackWidget: FeedbackWidgetConfig;
}

const DealCardUXContext = createContext<DealCardUXContextValue | null>(null);

// ================================================================
// PROVIDER
// ================================================================

interface DealCardUXProviderProps {
  children: ReactNode;
  cardSize?: CardSize;
  density?: CardDensity;
  /** CR130 Phase 3: Details mode */
  detailsMode?: DetailsMode;
  /** CR130 Phase 3: Expand single open mode */
  expandSingleOpen?: boolean;
  /** CR130 Phase 3: Details sections config */
  detailsSections?: DetailsSectionConfig[];
  /** CR171: Whether custom heights are enabled */
  customHeightsEnabled?: boolean;
  /** CR171: Custom heights per tier */
  customHeightsPx?: CustomHeightsPx;
  /** CR500 Phase 2B: Presentation mode configuration */
  detailsPresentation?: DetailsPresentationConfig;
  /** CR451: Flyer interaction config */
  flyerInteraction?: FlyerInteractionConfig;
  /** CR508: Global feedback widget placement config */
  feedbackWidget?: FeedbackWidgetConfig;
}

export function DealCardUXProvider({ 
  children, 
  cardSize = DEFAULT_DEAL_CARD_UX_CONFIG.cardSize,
  density = DEFAULT_DEAL_CARD_UX_CONFIG.density,
  detailsMode = DEFAULT_DEAL_CARD_UX_CONFIG.detailsMode,
  expandSingleOpen = DEFAULT_DEAL_CARD_UX_CONFIG.expandSingleOpen ?? true,
  detailsSections = DEFAULT_DEAL_CARD_UX_CONFIG.detailsSections,
  customHeightsEnabled = DEFAULT_DEAL_CARD_UX_CONFIG.customHeightsEnabled ?? false,
  customHeightsPx = DEFAULT_DEAL_CARD_UX_CONFIG.customHeightsPx ?? { compact: 200, regular: 400, large: 600 },
  detailsPresentation,
  flyerInteraction,
  feedbackWidget,
}: DealCardUXProviderProps) {
  // CR171: Use getDealCardHeightPx for computed height
  const cardHeightPx = getDealCardHeightPx(cardSize, customHeightsEnabled, customHeightsPx);
  
  const value: DealCardUXContextValue = {
    cardSize,
    density,
    cardHeightPx,
    spacing: DENSITY_SPACING[density],
    detailsMode,
    expandSingleOpen,
    detailsSections,
    customHeightsEnabled,
    customHeightsPx,
    detailsPresentation,
    flyerInteraction: flyerInteraction ?? DEFAULT_FLYER_INTERACTION_CONFIG,
    feedbackWidget: feedbackWidget ?? DEFAULT_FEEDBACK_WIDGET_CONFIG,
  };

  return (
    <DealCardUXContext.Provider value={value}>
      {children}
    </DealCardUXContext.Provider>
  );
}

// ================================================================
// HOOK
// ================================================================

/**
 * Get card UX config from context, or return defaults if not in a provider.
 * This allows DealGiftCard to work both with and without a provider.
 */
export function useDealCardUX(): DealCardUXContextValue {
  const context = useContext(DealCardUXContext);
  
  // Return defaults if not in a provider (backwards compatibility)
  if (!context) {
    const defaultCustomHeights = DEFAULT_DEAL_CARD_UX_CONFIG.customHeightsPx ?? { compact: 200, regular: 400, large: 600 };
    const defaultCustomEnabled = DEFAULT_DEAL_CARD_UX_CONFIG.customHeightsEnabled ?? false;
    return {
      cardSize: DEFAULT_DEAL_CARD_UX_CONFIG.cardSize,
      density: DEFAULT_DEAL_CARD_UX_CONFIG.density,
      cardHeightPx: getDealCardHeightPx(
        DEFAULT_DEAL_CARD_UX_CONFIG.cardSize,
        defaultCustomEnabled,
        defaultCustomHeights
      ),
      spacing: DENSITY_SPACING[DEFAULT_DEAL_CARD_UX_CONFIG.density],
      detailsMode: DEFAULT_DEAL_CARD_UX_CONFIG.detailsMode,
      expandSingleOpen: DEFAULT_DEAL_CARD_UX_CONFIG.expandSingleOpen ?? true,
      detailsSections: DEFAULT_DEAL_CARD_UX_CONFIG.detailsSections,
      customHeightsEnabled: defaultCustomEnabled,
      customHeightsPx: defaultCustomHeights,
      detailsPresentation: undefined,
      flyerInteraction: DEFAULT_FLYER_INTERACTION_CONFIG,
      feedbackWidget: DEFAULT_FEEDBACK_WIDGET_CONFIG,
    };
  }
  
  return context;
}

// ================================================================
// STYLE UTILITIES
// ================================================================

/**
 * Generate Tailwind classes for card container based on cardSize.
 * CR130 Hardening: Uses FIXED height (not min/max) to prevent content-driven growth.
 * 
 * NOTE: This function returns default height classes. For custom heights,
 * use getCardHeightStyle() instead which returns inline styles.
 * 
 * @deprecated Use getCardHeightStyle for custom height support
 */
export function getCardSizeClasses(cardSize: CardSize): string {
  // Fixed height ensures uniform grid rows regardless of content length
  switch (cardSize) {
    case "compact":
      return "h-[200px]"; // Fixed 200px
    case "regular":
      return "h-[400px]"; // Fixed 400px
    case "large":
      return "h-[600px]"; // Fixed 600px
    default:
      return "h-[400px]";
  }
}

/**
 * CR171: Get height style object for card container.
 * Supports custom heights when enabled, otherwise uses defaults.
 */
export function getCardHeightStyle(
  cardSize: CardSize,
  customHeightsEnabled?: boolean,
  customHeightsPx?: CustomHeightsPx
): { height: string } {
  const heightPx = getDealCardHeightPx(cardSize, customHeightsEnabled, customHeightsPx);
  return { height: `${heightPx}px` };
}

/**
 * Get height class string for use in external wrappers (e.g., flip mode).
 * @deprecated Use getCardHeightStyle for custom height support
 */
export function getCardHeightClass(cardSize: CardSize): string {
  return getCardSizeClasses(cardSize);
}

/**
 * Generate Tailwind classes for density-based spacing.
 */
export function getDensityClasses(density: CardDensity): {
  padding: string;
  gap: string;
  badgeGap: string;
  fontSize: string;
} {
  const spacing = DENSITY_SPACING[density];
  return {
    padding: `p-${spacing.cardPadding}`,
    gap: `gap-${spacing.sectionGap}`,
    badgeGap: `gap-${spacing.badgeGap}`,
    fontSize: `text-${spacing.fontSize}`,
  };
}

/**
 * Get line clamp class based on card size.
 * Smaller cards need more aggressive clamping.
 */
export function getTitleClampClass(cardSize: CardSize): string {
  switch (cardSize) {
    case "compact":
      return "line-clamp-1";
    case "regular":
      return "line-clamp-2";
    case "large":
      return "line-clamp-3";
    default:
      return "line-clamp-2";
  }
}

export function getHeadlineClampClass(cardSize: CardSize): string {
  switch (cardSize) {
    case "compact":
      return "line-clamp-1";
    case "regular":
      return "line-clamp-2";
    case "large":
      return "line-clamp-3";
    default:
      return "line-clamp-2";
  }
}

/**
 * Get header height based on card size (for the top band).
 */
export function getHeaderHeightClass(cardSize: CardSize): string {
  switch (cardSize) {
    case "compact":
      return "h-12"; // Reduced for compact
    case "regular":
      return "h-16"; // Balanced for regular
    case "large":
      return "h-20"; // Generous for large
    default:
      return "h-16";
  }
}

/**
 * Get flex-grow allocation class for the body section.
 * Ensures body fills remaining card space after header.
 */
export function getBodyFlexClass(): string {
  return "flex-1 min-h-0"; // flex-1 fills space, min-h-0 allows shrinking
}

/**
 * Check if this is running in a dev/admin context for dev-only UI.
 */
export function isDevEnvironment(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === "development";
}
